import React, { useState, useEffect } from 'react';
import api from '../api/client';
import './AddOrderPopup_SmallBags.css';

const AddOrderPopup_SmallBags = ({ sku, year, month_num, setName, statuses, suppliers, onClose, onSave, initialData }) => {
  const isEditing = !!initialData?.id;

  const [orderID, setOrderID]       = useState(initialData?.orderID || '');
  const [statusID, setStatusID]     = useState(initialData?.statusID || '');
  const [supplierID, setSupplierID] = useState(initialData?.supplierID || '');
  const [qty, setQty]               = useState(initialData?.qty?.toString() || '');
  const [pricePerPcs, setPricePerPcs] = useState(initialData?.price_per_pcs?.toString() || '');
  const [estDelDate, setEstDelDate] = useState(
    initialData?.est_del_date ? initialData.est_del_date.slice(0, 10) : ''
  );
  const [boFollowUpDate, setBoFollowUpDate] = useState(
    initialData?.bo_follow_up_date ? initialData.bo_follow_up_date.slice(0, 10) : ''
  );
  const [tracking, setTracking] = useState(initialData?.tracking || '');
  const [isSaving, setIsSaving] = useState(false);

  const total = (() => {
    const q = parseFloat(qty);
    const p = parseFloat(pricePerPcs);
    return (!isNaN(q) && !isNaN(p) && q > 0 && p > 0) ? (q * p).toFixed(2) : '0.00';
  })();

  const isBO = statusID === 'BO';

  const handleSubmit = async () => {
    if (!statusID || !supplierID || !qty || !pricePerPcs) {
      alert('Please fill all required fields');
      return;
    }

    const qtyNum  = parseInt(qty);
    const priceNum = parseFloat(pricePerPcs);

    if (isNaN(qtyNum) || isNaN(priceNum)) {
      alert('Quantity and Price per Piece must be valid numbers');
      return;
    }

    if (!isBO && !estDelDate) {
      alert('Please fill Estimated Delivery Date');
      return;
    }
    if (isBO && !boFollowUpDate) {
      alert('Please fill Backorder Follow-up Date');
      return;
    }

    const orderData = {
      ...(isEditing ? { id: initialData.id } : { sku, year, month_num, materialtypeID: 1 }),
      orderID: orderID.trim() || null,
      statusID,
      supplierID,
      qty: qtyNum,
      price_per_pcs: priceNum,
      est_del_date:      isBO ? null : estDelDate,
      bo_follow_up_date: isBO ? boFollowUpDate : null,
      tracking: tracking.trim() || null,
    };

    try {
      setIsSaving(true);
      await api.post('/api/add_order', orderData);
      onSave();
    } catch (error) {
      console.error('Error saving order:', error);
      alert(error.response?.data?.error || 'Failed to save order');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="sb-popup-overlay">
      <div className="sb-popup-modal">
        {/* Header */}
        <div className="sb-popup-header">
          <h2 className="sb-popup-title">
            <i className={`fa-solid ${isEditing ? 'fa-pen-to-square' : 'fa-box-open'}`} style={{ marginRight: 8 }}></i>
            {setName} <span style={{ opacity: 0.75 }}>({sku})</span>
          </h2>
          <p className="sb-popup-subtitle">
            {isEditing ? 'Edit Order' : 'New Order'} · {month_num}/{year}
          </p>
        </div>

        {/* Body */}
        <div className="sb-popup-body">
          {/* Order ID */}
          <div className="sb-form-group">
            <label className="sb-label">
              Order ID <span style={{ color: '#94a3b8', fontWeight: 400, textTransform: 'none' }}>(optional)</span>
            </label>
            <input
              className="sb-input"
              type="text"
              value={orderID}
              onChange={e => setOrderID(e.target.value)}
              placeholder="e.g. PO-2025-001"
            />
          </div>

          <hr className="sb-divider" />

          {/* Status + Supplier */}
          <div className="sb-form-row">
            <div className="sb-form-group">
              <label className="sb-label">Status <span className="required">*</span></label>
              <select
                className="sb-select"
                value={statusID}
                onChange={e => setStatusID(e.target.value)}
                required
              >
                <option value="">Select status</option>
                {statuses.map(s => (
                  <option key={s.statusID} value={s.statusID}>{s.status_name}</option>
                ))}
              </select>
            </div>
            <div className="sb-form-group">
              <label className="sb-label">Supplier <span className="required">*</span></label>
              <select
                className="sb-select"
                value={supplierID}
                onChange={e => setSupplierID(e.target.value)}
                required
              >
                <option value="">Select supplier</option>
                {suppliers.map(s => (
                  <option key={s.supplierID} value={s.supplierID}>{s.supplierName}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Qty + Price */}
          <div className="sb-form-row">
            <div className="sb-form-group">
              <label className="sb-label">Quantity <span className="required">*</span></label>
              <input
                className="sb-input"
                type="number"
                value={qty}
                onChange={e => setQty(e.target.value)}
                min="0"
                step="1"
                placeholder="0"
              />
            </div>
            <div className="sb-form-group">
              <label className="sb-label">Price per piece <span className="required">*</span></label>
              <input
                className="sb-input"
                type="number"
                step="0.0001"
                value={pricePerPcs}
                onChange={e => setPricePerPcs(e.target.value)}
                min="0"
                placeholder="0.0000"
              />
            </div>
          </div>

          {/* Total cost */}
          <div className="sb-total-banner">
            <span className="sb-total-label">
              <i className="fa-solid fa-calculator" style={{ marginRight: 6 }}></i>
              Total cost
            </span>
            <span className="sb-total-value">${total}</span>
          </div>

          {/* Date field — conditional */}
          {!isBO && (
            <div className="sb-form-group">
              <label className="sb-label">Estimated delivery date <span className="required">*</span></label>
              <input
                className="sb-input"
                type="date"
                value={estDelDate}
                onChange={e => setEstDelDate(e.target.value)}
              />
            </div>
          )}
          {isBO && (
            <div className="sb-form-group">
              <label className="sb-label">Backorder follow-up date <span className="required">*</span></label>
              <input
                className="sb-input"
                type="date"
                value={boFollowUpDate}
                onChange={e => setBoFollowUpDate(e.target.value)}
              />
            </div>
          )}

          {/* Tracking */}
          <div className="sb-form-group">
            <label className="sb-label">Tracking number</label>
            <input
              className="sb-input"
              type="text"
              value={tracking}
              onChange={e => setTracking(e.target.value)}
              placeholder="Optional"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="sb-popup-footer">
          <button className="sb-btn sb-btn-cancel" onClick={onClose}>
            Cancel
          </button>
          <button
            className="sb-btn sb-btn-save"
            onClick={handleSubmit}
            disabled={isSaving}
          >
            {isSaving
              ? <><i className="fa-solid fa-spinner fa-spin" style={{ marginRight: 6 }}></i>Saving...</>
              : isEditing
                ? <><i className="fa-solid fa-pen-to-square" style={{ marginRight: 6 }}></i>Update Order</>
                : <><i className="fa-solid fa-floppy-disk" style={{ marginRight: 6 }}></i>Save Order</>
            }
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddOrderPopup_SmallBags;